import { z } from 'zod';

import { makeListQuery } from '../../common/list-query/list-query';
import { LikeDecisionSchema } from '../like-decision/like-decision';
import { MatchStatusSchema } from '../match-status/match-status';

/**
 * The only column either likes list may be ordered by.
 *
 * Whitelisted rather than left as `PaginationQuerySchema`'s free string: a sort
 * key that is not a column is either a silent fallback to some other order — the
 * member scrolls a list that is not the one they asked for — or a 500. Neither
 * is a thing a client should be able to cause by typing.
 */
export const LIKE_SORT_FIELDS = ['decidedAt'] as const;

/**
 * "Who liked me" and "who I liked" (N7).
 *
 * `sort` is fixed to the decision time and defaults to **newest first**, because
 * that is the only order these two screens are ever read in — and stating the
 * default here rather than leaving it to the server keeps the client's own
 * control in step with what it will actually receive (P15b).
 *
 * `decision` is the one filter. It is meaningful on the *sent* list ("show me
 * who I passed on"); the *received* list narrows to likes below the filter
 * layer, from the endpoint, because a member is never shown who passed on them —
 * so a client naming `decision=pass` there gets an empty page rather than a
 * disclosure.
 */
export const LikesListQuerySchema = makeListQuery({
  decision: LikeDecisionSchema.optional(),
}).extend({
  sort: z.enum(LIKE_SORT_FIELDS).default('decidedAt'),
});

export type LikesListQuery = z.infer<typeof LikesListQuerySchema>;

/** The only column the matches list may be ordered by. */
export const MATCH_SORT_FIELDS = ['matchedAt'] as const;

/**
 * The member's matches.
 *
 * `status` is whitelisted but has **no default**, so the endpoint decides what
 * "my matches" means (the live ones) and a member who wants to look back at an
 * ended one has to ask. A default of `active` in the contract would read as if
 * ended matches were unreachable; the absence says the endpoint owns it.
 */
export const MatchesListQuerySchema = makeListQuery({
  status: MatchStatusSchema.optional(),
}).extend({
  sort: z.enum(MATCH_SORT_FIELDS).default('matchedAt'),
});

export type MatchesListQuery = z.infer<typeof MatchesListQuerySchema>;
